import { VisibilityOffTwoTone } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Zoom
} from '@mui/material';
import { useFormik } from 'formik';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import useRefMounted from 'src/hooks/useRefMounted';
import { useResetPasswordMutation } from 'src/services/user-management/user-services';
import * as Yup from 'yup';
import { Transition } from '../Commons';

type Props = {
  open: boolean;
  userId: number;
  handleClose: () => void;
};

const ResetPasswordAdmin = ({ open, handleClose, userId }: Props) => {
  const isMountedRef = useRefMounted();
  const [resetPasswordReq, resetPasswordRes] = useResetPasswordMutation();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const resetPasswordForm = useFormik({
    initialValues: {
      password: '',
      userId: userId,
      submit: null
    },
    validationSchema: Yup.object().shape({
      password: Yup.string().required(t('The password field is required'))
      // .min(8, t('Password must be 8 characters long'))
      // .matches(/[0-9]/, t('Password requires a number'))
      // .matches(/[a-z]/, t('Password requires a lowercase letter'))
      // .matches(/[A-Z]/, t('Password requires an uppercase letter'))
      // .matches(/[^\w]/, t('Password requires a symbol'))
    }),
    onSubmit: async (_values, { setSubmitting }) => {
      setSubmitting(true);
      await resetPassword(_values);
      setSubmitting(false);
    }
  });

  const resetPassword = useCallback(
    async (values: typeof resetPasswordForm.values) => {
      try {
        const payload: any = {
          password: values.password,
          userId: values.userId
        };

        await resetPasswordReq(payload)
          .unwrap()
          .then(() => {});
      } catch (err) {
        const errormessage = err.data?.serviceErrors[0]?.msg;

        enqueueSnackbar(errormessage, {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right'
          },
          TransitionComponent: Zoom
        });
      }
    },
    [resetPasswordForm.values, isMountedRef]
  );

  useEffect(() => {
    if (resetPasswordRes.isSuccess) {
      handleClose();
      enqueueSnackbar(t('Password changed successfully'), {
        autoHideDuration: 3000,
        variant: 'success',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right'
        },
        TransitionComponent: Zoom
      });
    }
  }, [resetPasswordRes.isSuccess]);

  useEffect(() => {
    return () => {
      resetPasswordForm.resetForm();
    };
  }, [open]);

  return (
    <Dialog
      TransitionComponent={Transition}
      fullWidth
      open={open}
      onClose={handleClose}
    >
      <Card
        sx={{
          p: 4
        }}
      >
        <Box>
          <Typography
            variant="h2"
            sx={{
              mb: 2
            }}
          >
            {t('Change Your Password')}
          </Typography>
          <Typography
            variant="h4"
            color="text.secondary"
            fontWeight="normal"
            sx={{
              mb: 1
            }}
          >
            {t('Enter your new password')}
          </Typography>
        </Box>
        <form noValidate onSubmit={resetPasswordForm.handleSubmit}>
          <TextField
            error={Boolean(
              resetPasswordForm.touched.password &&
                resetPasswordForm.errors.password
            )}
            fullWidth
            helperText={
              resetPasswordForm.touched.password &&
              resetPasswordForm.errors.password
            }
            label={t('Password')}
            name="password"
            onBlur={resetPasswordForm.handleBlur}
            onChange={resetPasswordForm.handleChange}
            type={showPassword ? 'text' : 'password'}
            value={resetPasswordForm.values.password}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    {showPassword ? (
                      <VisibilityIcon />
                    ) : (
                      <VisibilityOffTwoTone />
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Stack direction={'row'} gap={2} justifyContent={'center'}>
            <Button
              sx={{
                mt: 3
              }}
              color="primary"
              startIcon={
                resetPasswordForm.isSubmitting ? (
                  <CircularProgress size="1rem" />
                ) : null
              }
              type="submit"
              size="large"
              variant="contained"
            >
              {t('Reset Password')}
            </Button>
            <Button sx={{ mt: 3 }} color="primary" onClick={handleClose}>
              {t('Cancel')}
            </Button>
          </Stack>
        </form>
      </Card>
    </Dialog>
  );
};

export default ResetPasswordAdmin;
